import Color from 'color';
import styled, {css} from 'styled-components';

import type {ThemeAndAnyColor, ThemeColor} from '../../../styles/theme';
import type {IconVariant, SizeVariant, Variant} from './types';

import MoreSvg from '../../../assets/icons/more.svg';
import TrashIcon from '../../../assets/icons/secondary/Delete.svg';
import {ms, msi} from '../../../styles/normalizeSize';
import {getColor} from '../../../styles/utils';
import {Clickable} from '../../../uikit/Clickable';
import {Image} from '../../../uikit/Image';
import {Text} from '../../../uikit/Text';
import {View} from '../../../uikit/View';
import {platform} from '../../../utils/platform';

export const Container = styled(View)`
  flex-direction: row;
  align-items: center;
`;

export const Content = styled(View)`
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding-right: ${ms(10)};
  overflow: hidden;
`;

export const Attachment = styled(Image)<{
  iconFillColor?: string;
  iconVariant: IconVariant;
  isImage?: boolean;
  sizeVariant?: SizeVariant;
  variant?: Variant;
}>`
  background-color: ${getColor('background1')};
  width: ${ms(48)};
  height: ${ms(48)};
  ${p =>
    p.iconVariant === 'square' &&
    css`
      border-radius: ${ms(10)};
    `};
  ${p =>
    p.iconVariant === 'circle' &&
    css`
      border-radius: ${ms(48)};
    `};
  align-items: center;
  justify-content: center;
  margin-right: ${ms(10)};

  ${p =>
    !p.isImage &&
    p.variant !== 'coloured' &&
    css`
      border: 1px solid ${getColor('border2')};
    `}

  ${p =>
    p.variant === 'coloured' &&
    css`
      background-color: ${Color(p.iconFillColor).alpha(0.2).toString()};
    `}

  ${p =>
    p.sizeVariant === 's' &&
    css`
      width: ${ms(32)};
      height: ${ms(32)};
    `}
`;

export const Placeholder = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const getIconStyle = (size?: SizeVariant) => {
  if (size === 's') {
    return {
      height: msi(20),
      width: msi(20),
    };
  }

  return {
    height: msi(24),
    width: msi(24),
  };
};

export const Name = styled(Text).attrs({
  nativeProps: {numberOfLines: 1},
  weight: 'bold',
})<{
  accentColor?: ThemeAndAnyColor;
  sizeVariant?: SizeVariant;
}>`
  color: ${getColor('text1')};
  ${p =>
    p.accentColor &&
    css`
      color: ${getColor(p.accentColor as ThemeColor)(p) ?? p.accentColor};
    `}
  font-size: ${ms(14)};
  line-height: ${ms(17)};
  letter-spacing: ${ms(-0.24)};

  ${platform.isWeb &&
  css`
    overflow-wrap: anywhere;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    overflow: hidden;
  `}
`;

export const Label = styled(Text).attrs({nativeProps: {numberOfLines: 1}})<{
  sizeVariant?: SizeVariant;
}>`
  color: ${getColor('text1')};
  font-size: ${ms(12)};
  line-height: ${ms(14)};
  letter-spacing: ${ms(-0.08)};
  margin-top: ${ms(4)};

  ${p =>
    p.sizeVariant === 's' &&
    css`
      margin-top: ${ms(0)};
      font-size: ${ms(10)};
      line-height: ${ms(12)};
    `}
`;

export const ClickableIconContainer = styled(Clickable)`
  align-items: center;
  justify-content: center;
`;

export const DeleteIcon = styled(TrashIcon).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(20)};
  height: ${ms(20)};
`;

export const MoreIcon = styled(MoreSvg).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(20)};
  height: ${ms(20)};
`;
